import { useCallback, useEffect, useRef } from 'react';

/**
 * Creates a debounced version of a callback function that delays invoking the callback
 * until after `delay` milliseconds have elapsed since the last time the debounced
 * function was invoked.
 *
 * This is a low-level, general-purpose debounce hook. For search-specific functionality
 * with state management, use `useDebouncedSearch` instead.
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds (default: 300ms)
 * @returns A debounced version of the callback function
 *
 * @example
 * ```tsx
 * // Debouncing API calls
 * const debouncedSave = useDebouncedCallback((data: FormData) => {
 *   saveToAPI(data);
 * }, 500);
 *
 * <Form onChange={debouncedSave} />
 * ```
 *
 * @example
 * ```tsx
 * // Debouncing slider changes
 * const debouncedUpdate = useDebouncedCallback((value: number) => {
 *   updateSensitivity(value);
 * }, 500);
 *
 * <Range onValueChange={debouncedUpdate} />
 * ```
 */
export function useDebouncedCallback<T extends (...args: never[]) => unknown>(
  callback: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref up to date to avoid stale closures
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: Parameters<T>) => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );
}
